import React, { useCallback } from "react";

import { Grid, MenuItem } from "@mui/material";
import { Divider } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../../../../context/StateContext";
import { DifferentLocation } from "../../../../../../models/location";
import { NotifyParty } from "../../../../../../models/notify.party";
import { Office } from "../../../../../../models/office";
import { Port } from "../../../../../../models/port";
import { Shipment } from "../../../../../../models/shipments";
import { ShipmentDetails } from "../../../../../../util/enums";
import { Input, Select } from "../../../../../Form";
import AutoSuggest from "../../../../../Form/AutoSuggest";
import MapInput from "../../../../../Form/MapInput";
import SkeletonComponent from "../../../../../Generic/Skeleton";
import getShipmentOptions from "../../../../../Shipments/hooks/getShipmentOptions";
import { useBookingContext } from "../../../../context/Booking";
import { BookingObjects, formatPortInputValue } from "../../../../util";
import useStyles from "../../styles";
import { DetailsSectionsProps, getExtendedFormInputInfoByType } from "../../types";
import { DetailsSectionTitle } from "../SectionTitle";

interface DetailsGeneralSectionProps extends DetailsSectionsProps {
  getOfficeInputValue: () => string | undefined;
  onUpdateAllShipment: (value: Shipment) => void;
  updateOfficeBasedOnLoadingPort: (port: Port) => void;
  loadingResourcesByVersion: boolean;
  officeFromLoadingPort: Office | undefined;
  officeList: Office[];
  notifyPartySelected: NotifyParty | undefined;
  notifyPartyList: NotifyParty[];
  portList: Port[];
  purchaseOrders: string;
  uniqueOffices: Office[];
}

export const DetailsShipmentSection = ({
  generateBaseFieldProps,
  getOfficeInputValue,
  onUpdateShipmentDetails,
  onUpdateAllShipment,
  updateOfficeBasedOnLoadingPort,
  data,
  loadingResourcesByVersion,
  officeFromLoadingPort,
  officeList,
  purchaseOrders,
  portList,
  uniqueOffices,
  notifyPartyList,
  notifyPartySelected,
  viewOnly,
}: DetailsGeneralSectionProps) => {
  const { t: translate } = useTranslation();
  const { handleChangeOptions } = useBookingContext();
  const { isFMorSPVersion } = useAppState();
  const { typeOptions: modeOptions, shipTermsOptions } = getShipmentOptions(false);
  const classes = useStyles();

  const loadingPortSelected = isFMorSPVersion
    ? data?.portOfLoading
    : portList.find((port) => Number(data?.portOfLoadingId) === Number(port.id));

  const dischargePortSelected = isFMorSPVersion
    ? data?.portOfDischarge
    : portList.find((port) => Number(data?.portOfDischargeId) === Number(port.id));

  const receiptPortSelected = isFMorSPVersion
    ? data?.portOfReceipt
    : portList.find((port) => Number(data?.portOfReceiptId) === Number(port.id));

  const destinationPortSelected = isFMorSPVersion
    ? data?.portOfDestination
    : portList.find((port) => Number(data?.portOfDestinationId) === Number(port.id));

  const cleanTypeFields = () => {
    onUpdateShipmentDetails(ShipmentDetails.ORIGIN, null);
    onUpdateShipmentDetails(ShipmentDetails.DESTINATION, null);
    onUpdateShipmentDetails(ShipmentDetails.PORT_OF_LOADING_ID, null);
    onUpdateShipmentDetails(ShipmentDetails.PORT_OF_DESTINATION_ID, null);
    onUpdateShipmentDetails(ShipmentDetails.PORT_OF_DISCHARGE_ID, null);
    onUpdateShipmentDetails(ShipmentDetails.PORT_OF_RECEIPT_ID, null);
  };

  const onSelectAddress = useCallback(
    (type: ShipmentDetails.ORIGIN | ShipmentDetails.DESTINATION, location?: DifferentLocation) => {
      if (!location) return;

      onUpdateAllShipment({
        [type]: location.longAddress,
        [`${type}Coordinates`]: {
          lat: location.latitude || 0,
          lng: location.longitude || 0,
        },
      });
      if (handleChangeOptions) handleChangeOptions(BookingObjects.BOOKING);
    },
    [data, onUpdateAllShipment]
  );

  const getExtendedFormTitleByType = useCallback((type?: string) => {
    if (!type) return "";
    switch (type?.toLowerCase()) {
      case "air":
        return translate("airports");
      case "sea":
        return translate("ports");
      case "road":
        return translate("roads");
      default:
        return "";
    }
  }, []);

  const renderExtendedPortFormByType = () => {
    if (data?.shipmentType) {
      const title = getExtendedFormTitleByType(data?.shipmentType);

      if (!title) return null;

      const info = getExtendedFormInputInfoByType(data?.shipmentType, translate);

      if (!info) return null;

      if (data?.shipmentType.toLowerCase() === "air") {
        const airPortList = portList.filter((port) => port.site === "AIR");

        return (
          <Grid container item spacing={2}>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SkeletonComponent isLoading={loadingResourcesByVersion}>
                <AutoSuggest
                  {...generateBaseFieldProps(ShipmentDetails.PORT_OF_LOADING_ID)}
                  label={info.origin.label}
                  placeholder={info.origin.placeholder}
                  inputValue={formatPortInputValue(loadingPortSelected)}
                  options={airPortList || []}
                  getOptionSelected={(option?: Port, value?: Port) => option?.id === value?.id}
                  getOptionLabel={(port: Port) => formatPortInputValue(port)}
                  onChange={(port: Port) => {
                    updateOfficeBasedOnLoadingPort(port);
                    if (port && port.id) {
                      onUpdateShipmentDetails(ShipmentDetails.PORT_OF_LOADING_ID, port.id);
                    } else {
                      onUpdateShipmentDetails(ShipmentDetails.PORT_OF_LOADING_ID, null);
                    }
                  }}
                  required
                  dataCy={info.origin.dataCy}
                />
              </SkeletonComponent>
            </Grid>

            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SkeletonComponent isLoading={loadingResourcesByVersion}>
                <AutoSuggest
                  {...generateBaseFieldProps(ShipmentDetails.PORT_OF_DISCHARGE_ID)}
                  label={info.discharge?.label}
                  placeholder={info.discharge?.placeholder}
                  inputValue={formatPortInputValue(dischargePortSelected)}
                  options={airPortList || []}
                  getOptionSelected={(option?: Port, value?: Port) => option?.id === value?.id}
                  getOptionLabel={(port: Port) => formatPortInputValue(port)}
                  onChange={(port: Port) => {
                    if (port && port.id) {
                      onUpdateShipmentDetails(ShipmentDetails.PORT_OF_DISCHARGE_ID, port.id);
                    } else {
                      onUpdateShipmentDetails(ShipmentDetails.PORT_OF_DISCHARGE_ID, null);
                    }
                  }}
                  required
                  dataCy={info.discharge?.dataCy}
                />
              </SkeletonComponent>
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SkeletonComponent isLoading={loadingResourcesByVersion}>
                <AutoSuggest
                  {...generateBaseFieldProps(ShipmentDetails.PORT_OF_RECEIPT_ID)}
                  label={info.receipt?.label}
                  placeholder={info.receipt?.placeholder}
                  inputValue={formatPortInputValue(receiptPortSelected)}
                  options={airPortList || []}
                  getOptionSelected={(option?: Port, value?: Port) => option?.id === value?.id}
                  getOptionLabel={(port: Port) => formatPortInputValue(port)}
                  onChange={(port: Port) => {
                    if (port && port.id) {
                      onUpdateShipmentDetails(ShipmentDetails.PORT_OF_RECEIPT_ID, port.id);
                    } else {
                      onUpdateShipmentDetails(ShipmentDetails.PORT_OF_RECEIPT_ID, null);
                    }
                  }}
                  required
                  dataCy={info.receipt?.dataCy}
                />
              </SkeletonComponent>
            </Grid>

            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SkeletonComponent isLoading={loadingResourcesByVersion}>
                <AutoSuggest
                  {...generateBaseFieldProps(ShipmentDetails.PORT_OF_DESTINATION_ID)}
                  label={info.destination.label}
                  placeholder={info.destination.placeholder}
                  inputValue={formatPortInputValue(destinationPortSelected)}
                  options={airPortList || []}
                  getOptionSelected={(option?: Port, value?: Port) => option?.id === value?.id}
                  getOptionLabel={(port: Port) => formatPortInputValue(port)}
                  onChange={(port: Port) => {
                    if (port && port.id) {
                      onUpdateShipmentDetails("all", {
                        ...data,
                        portOfDestinationId: port.id,
                        portOfDestination: port,
                      });
                    } else {
                      onUpdateShipmentDetails("all", {
                        ...data,
                        portOfDestinationId: null,
                        portOfDestination: null,
                      });
                    }
                  }}
                  required
                  dataCy={info.destination?.dataCy}
                />
              </SkeletonComponent>
            </Grid>
          </Grid>
        );
      }

      if (data?.shipmentType.toLowerCase() === "sea") {
        const seaPortList = portList.filter((port) => port.site !== "AIR");

        return (
          <Grid container item spacing={2}>
            <Grid item xs={12}>
              <Divider orientation={"horizontal"} flexItem className={classes.dividerHorizontal} />
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SkeletonComponent isLoading={loadingResourcesByVersion}>
                <AutoSuggest
                  {...generateBaseFieldProps(ShipmentDetails.PORT_OF_LOADING_ID)}
                  label={info.origin.label}
                  placeholder={info.origin.placeholder}
                  inputValue={formatPortInputValue(loadingPortSelected)}
                  options={seaPortList || []}
                  getOptionSelected={(option?: Port, value?: Port) => option?.id === value?.id}
                  getOptionLabel={(port: Port) => formatPortInputValue(port)}
                  onChange={(port: Port) => {
                    updateOfficeBasedOnLoadingPort(port);
                    if (port && port.id) {
                      onUpdateShipmentDetails(ShipmentDetails.PORT_OF_LOADING_ID, port.id);
                    } else {
                      onUpdateShipmentDetails(ShipmentDetails.PORT_OF_LOADING_ID, null);
                    }
                  }}
                  required
                  dataCy={info.origin.dataCy}
                />
              </SkeletonComponent>
            </Grid>

            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SkeletonComponent isLoading={loadingResourcesByVersion}>
                <AutoSuggest
                  {...generateBaseFieldProps(ShipmentDetails.PORT_OF_DISCHARGE_ID)}
                  label={info.discharge?.label}
                  placeholder={info.discharge?.placeholder}
                  inputValue={formatPortInputValue(dischargePortSelected)}
                  options={seaPortList || []}
                  getOptionSelected={(option?: Port, value?: Port) => option?.id === value?.id}
                  getOptionLabel={(port: Port) => formatPortInputValue(port)}
                  onChange={(port: Port) => {
                    if (port && port.id) {
                      onUpdateShipmentDetails(ShipmentDetails.PORT_OF_DISCHARGE_ID, port.id);
                    } else {
                      onUpdateShipmentDetails(ShipmentDetails.PORT_OF_DISCHARGE_ID, null);
                    }
                  }}
                  required
                  dataCy={info.discharge?.dataCy}
                />
              </SkeletonComponent>
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SkeletonComponent isLoading={loadingResourcesByVersion}>
                <AutoSuggest
                  {...generateBaseFieldProps(ShipmentDetails.PORT_OF_RECEIPT_ID)}
                  label={info.receipt?.label}
                  placeholder={info.receipt?.placeholder}
                  inputValue={formatPortInputValue(receiptPortSelected)}
                  options={seaPortList || []}
                  getOptionSelected={(option?: Port, value?: Port) => option?.id === value?.id}
                  getOptionLabel={(port: Port) => formatPortInputValue(port)}
                  onChange={(port: Port) => {
                    if (port && port.id) {
                      onUpdateShipmentDetails(ShipmentDetails.PORT_OF_RECEIPT_ID, port.id);
                    } else {
                      onUpdateShipmentDetails(ShipmentDetails.PORT_OF_RECEIPT_ID, null);
                    }
                  }}
                  required
                  dataCy={info.receipt?.dataCy}
                />
              </SkeletonComponent>
            </Grid>

            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SkeletonComponent isLoading={loadingResourcesByVersion}>
                <AutoSuggest
                  {...generateBaseFieldProps(ShipmentDetails.PORT_OF_DESTINATION_ID)}
                  label={info.destination.label}
                  placeholder={info.destination.placeholder}
                  inputValue={formatPortInputValue(destinationPortSelected)}
                  options={seaPortList || []}
                  getOptionSelected={(option?: Port, value?: Port) => option?.id === value?.id}
                  getOptionLabel={(port: Port) => formatPortInputValue(port)}
                  onChange={(port: Port) => {
                    if (port && port.id) {
                      onUpdateShipmentDetails("all", {
                        ...data,
                        portOfDestinationId: port.id,
                        portOfDestination: port,
                      });
                    } else {
                      onUpdateShipmentDetails("all", {
                        ...data,
                        portOfDestinationId: null,
                        portOfDestination: null,
                      });
                    }
                  }}
                  required
                  dataCy={info.destination?.dataCy}
                />
              </SkeletonComponent>
            </Grid>
          </Grid>
        );
      }

      return (
        <Grid container item spacing={2}>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <MapInput
              inputValue={data?.origin}
              onChange={(location) => onSelectAddress(ShipmentDetails.ORIGIN, location)}
              onChangeInput={(value) => onUpdateShipmentDetails(ShipmentDetails.ORIGIN, value)}
              showMapIcon={false}
              disabled={viewOnly}
              label={info.origin.label}
              placeholder={info.origin.placeholder}
              noOptionsText={translate("searchAddress")}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <MapInput
              inputValue={data?.destination}
              onChange={(location) => onSelectAddress(ShipmentDetails.DESTINATION, location)}
              onChangeInput={(value) => onUpdateShipmentDetails(ShipmentDetails.DESTINATION, value)}
              showMapIcon={false}
              disabled={viewOnly}
              label={info.destination.label}
              placeholder={info.destination.placeholder}
              noOptionsText={translate("searchAddress")}
            />
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  const renderLastRowFormByType = () => {
    if (!data?.shipmentType) return null;
    switch (data?.shipmentType.toLowerCase()) {
      case "air":
        return (
          <Grid container item spacing={2}>
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Input
                {...generateBaseFieldProps(ShipmentDetails.PLANE)}
                value={data?.plane || ""}
                onChange={(event) =>
                  onUpdateShipmentDetails(ShipmentDetails.PLANE, event.target.value)
                }
                placeholder={translate("placeName")}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Input
                {...generateBaseFieldProps(ShipmentDetails.JOURNEY)}
                value={data?.journey || ""}
                onChange={(event) =>
                  onUpdateShipmentDetails(ShipmentDetails.JOURNEY, event.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Input
                {...generateBaseFieldProps(ShipmentDetails.AIRWAY_BILL)}
                value={data?.airwayBill || ""}
                onChange={(event) =>
                  onUpdateShipmentDetails(ShipmentDetails.AIRWAY_BILL, event.target.value)
                }
              />
            </Grid>
          </Grid>
        );
      case "sea":
        return (
          <Grid container item spacing={2}>
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Input
                {...generateBaseFieldProps(ShipmentDetails.VESSEL_NAME)}
                value={data?.vesselName || ""}
                label={translate("vessel")}
                onChange={(event) =>
                  onUpdateShipmentDetails(ShipmentDetails.VESSEL_NAME, event.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Input
                {...generateBaseFieldProps(ShipmentDetails.VOYAGE_NUMBER)}
                value={data?.voyageNumber || ""}
                label={translate("voyage")}
                onChange={(event) =>
                  onUpdateShipmentDetails(ShipmentDetails.VOYAGE_NUMBER, event.target.value)
                }
                placeholder={translate("voyage")}
                data-cy={ShipmentDetails.VOYAGE_NUMBER}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Input
                {...generateBaseFieldProps(ShipmentDetails.MBL)}
                value={data?.mbl || ""}
                label={`${translate(ShipmentDetails.MBL)} #`}
                onChange={(event) =>
                  onUpdateShipmentDetails(ShipmentDetails.MBL, event.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Input
                {...generateBaseFieldProps(ShipmentDetails.HBL)}
                value={data?.hbl || ""}
                onChange={(event) =>
                  onUpdateShipmentDetails(ShipmentDetails.HBL, event.target.value)
                }
              />
            </Grid>
          </Grid>
        );
      case "road":
      default:
        return null;
    }
  };

  return (
    <Grid container item spacing={2}>
      <Grid item xs={12}>
        <Divider orientation={"horizontal"} flexItem className={classes.dividerHorizontal} />
      </Grid>
      <DetailsSectionTitle title={translate("shipment")} />
      <Grid container item spacing={2}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Select
            {...generateBaseFieldProps(ShipmentDetails.SHIPMENT_TYPE)}
            label={translate("mode")}
            value={data?.shipmentType || ""}
            onChange={(event) => {
              onUpdateShipmentDetails(ShipmentDetails.SHIPMENT_TYPE, event.target.value);
              cleanTypeFields();
            }}
            displayEmpty
            placeholder={translate("selectOne")}
            required
          >
            {modeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Input
            {...generateBaseFieldProps(ShipmentDetails.PURCHASE_ORDERS)}
            value={purchaseOrders}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Select
            {...generateBaseFieldProps(ShipmentDetails.SHIP_TERMS)}
            required
            displayEmpty
            placeholder={translate("selectOne")}
            value={data?.shipTerms || ""}
            onChange={(event) =>
              onUpdateShipmentDetails(ShipmentDetails.SHIP_TERMS, event.target.value)
            }
          >
            {shipTermsOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>

      {renderLastRowFormByType()}
      <Grid container item spacing={2}>
        <Grid item xs={12} sm={3} md={3} lg={3}>
          <SkeletonComponent isLoading={loadingResourcesByVersion}>
            <AutoSuggest
              {...generateBaseFieldProps(ShipmentDetails.OFFICE_ID)}
              required
              label={translate("office")}
              placeholder={translate("selectOne")}
              inputValue={getOfficeInputValue()}
              options={officeFromLoadingPort ? officeList : uniqueOffices || []}
              getOptionSelected={(option?: Office, value?: Office) => option?.id === value?.id}
              getOptionLabel={(office: Office) => `${office?.name} - ${office?.beCode}` || ""}
              onChange={(office: Office) => {
                if (office && office.id) {
                  onUpdateShipmentDetails(ShipmentDetails.OFFICE_ID, office.id);
                } else {
                  onUpdateShipmentDetails(ShipmentDetails.OFFICE_ID, null);
                }
              }}
              disabled={viewOnly || !!officeFromLoadingPort}
              dataCy={ShipmentDetails.OFFICE_ID}
            />
          </SkeletonComponent>
        </Grid>
        <Grid item xs={12} sm={3} md={3} lg={3}>
          <SkeletonComponent isLoading={loadingResourcesByVersion}>
            <AutoSuggest
              {...generateBaseFieldProps(ShipmentDetails.NOTIFY_PARTY_ID)}
              label={translate("notifyParty")}
              placeholder={translate("selectOne")}
              inputValue={notifyPartySelected?.name}
              options={notifyPartyList || []}
              getOptionSelected={(option?: NotifyParty, value?: NotifyParty) =>
                option?.id === value?.id
              }
              getOptionLabel={(option: NotifyParty) => option.name || ""}
              onChange={(option: NotifyParty) => {
                if (option && option.id) {
                  onUpdateShipmentDetails(ShipmentDetails.NOTIFY_PARTY_ID, option.id);
                } else {
                  onUpdateShipmentDetails(ShipmentDetails.NOTIFY_PARTY_ID, null);
                }
              }}
            />
          </SkeletonComponent>
        </Grid>
      </Grid>

      {renderExtendedPortFormByType()}
    </Grid>
  );
};
